<template>
  <div class="wrapper">
    <div class="main visual-2 doctor-bio">
      <Header :backRoute="'/about-hospital'" />
      <div class="container py-4 bio-container">
        <!-- Loading -->
        <div
          v-if="loading"
          class="d-flex justify-content-center align-items-center"
          style="min-height: 300px"
        >
          <div
            class="spinner-border text-primary"
            role="status"
            style="width: 4rem; height: 4rem"
          >
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>

        <!-- Doctor Found -->
        <div v-else-if="doctor">
          <div class="card rounded p-4">
            <!-- Top Section -->
            <div class="row align-items-center">
              <!-- Left: Doctor Image + Info -->
              <div
                class="col-12 col-md-6 d-flex align-items-center mb-3 mb-md-0"
              >
                <img
                  :src="doctorImage"
                  alt="Doctor Avatar"
                  class="img-fluid rounded border me-3"
                  style="max-width: 120px"
                />
                <div>
                  <h3 class="fw-bold mb-1">
                    {{ doctor.firstname.trim() }} {{ doctor.lastname.trim() }}
                  </h3>
                  <p class="text-muted mb-1 fs-5">
                    <i class="fa-solid fa-graduation-cap"></i>
                    {{ doctor.speciality || "N/A" }}
                  </p>
                  <p class="text-muted mb-0 fs-5">
                    <i class="fas fa-map-marker-alt"></i>
                    {{ hospitalAddress }}
                  </p>
                  <p>
                    <i class="fa-solid fa-money-bill"></i>
                    {{
                      $formatHospitalCurrency(
                        doctor?.consultationFee,
                        doctor?.hospital?.id || doctor?.hospitalId
                      )
                    }}
                  </p>
                </div>
              </div>

              <!-- Right: Stats -->
              <div class="col-12 col-md-6">
                <div
                  class="d-flex justify-content-md-end justify-content-center flex-wrap text-center"
                >
                  <div class="me-5 mb-2">
                    <img
                      src="@/assets/img/doc-profile-pg-icn-1.png"
                      alt="Patients"
                      style="width: 30px"
                      class="mb-1"
                    />
                    <div class="fw-bold doc-sp">
                      {{ doctor.timeSlots[0]?.numberOfPatients || "N/A" }}
                    </div>
                    <div class="text-muted fs-6">Patients</div>
                  </div>
                  <div class="me-5 mb-2">
                    <img
                      src="@/assets/img/doc-profile-pg-icn-2.png"
                      alt="Exp"
                      style="width: 30px"
                      class="mb-1"
                    />
                    <div class="fw-bold doc-sp">
                      {{ doctor.experienceYears || "N/A" }}+
                    </div>
                    <div class="text-muted fs-6">Years Exp.</div>
                  </div>
                  <!-- <div class="me-5 mb-2">
                    <img
                      src="@/assets/img/doc-profile-pg-icn-3.png"
                      alt="Rating"
                      style="width: 30px"
                      class="mb-1"
                    />
                    <div class="fw-bold doc-sp">{{ averageRating }}</div>
                    <div class="text-muted fs-6">Rating</div>
                  </div>
                  <div class="mb-2">
                    <img
                      src="@/assets/img/doc-profile-pg-icn-4.png"
                      alt="Reviews"
                      style="width: 30px"
                      class="mb-1"
                    />
                    <div class="fw-bold doc-sp">N/A</div>
                    <div class="text-muted fs-6">Reviews</div>
                  </div> -->
                </div>
              </div>
            </div>

            <hr class="my-4" />

            <!-- Working Hours -->
            <div>
              <h5 class="fw-bold">Working Hours</h5>
              <div
                v-if="doctor.timeSlots && doctor.timeSlots.length"
                class="doc-wd"
              >
                <ul class="list-group">
                  <li
                    v-for="(slot, index) in uniqueTimeSlotsByHospital"
                    :key="index"
                    class="list-group-item"
                  >
                    <div
                      v-for="(dayObj, dayIndex) in getUniqueDays(slot.days)"
                      :key="`${index}-${dayIndex}`"
                      class="list-group-item d-flex justify-content-between align-items-center"
                    >
                      <span class="fw-bold">{{ dayObj.day }}</span>
                      <span class="text-muted">
                        {{ convertUtcToLocal(dayObj.startTime) }} -
                        {{ convertUtcToLocal(dayObj.endTime) }}
                      </span>
                    </div>
                  </li>
                </ul>
              </div>
              <div v-else class="text-muted">No working hours available.</div>
            </div>

            <!-- About -->
            <div class="mb-4">
              <h5 class="fw-bold">About</h5>
              <div
                class="text-muted fs-5"
                v-html="doctor.aboutMe || 'No bio available.'"
              ></div>
            </div>
          </div>
        </div>

        <!-- Doctor Not Found -->
        <div v-else class="text-center p-5">Doctor not found.</div>
      </div>
      <FooterView />
    </div>
  </div>
</template>

<script>
import FooterView from "@/layout/Footer.vue";
import Header from "@/layout/Header.vue";
import { DoctorsService } from "@/services/doctors.service";

export default {
  name: "DoctorBio",
  components: {
    FooterView,
    Header,
  },
  data() {
    return {
      doctor: null,
      loading: true,
      error: null,
      hospitalMap: {}, // key: hospitalId, value: hospital object
    };
  },
  computed: {
    hospital() {
      const hospitals = Object.values(this.hospitalMap);
      return hospitals.length ? hospitals[0] : null;
    },
    hospitalAddress() {
      return this.hospital?.address || "No address";
    },
    averageRating() {
      return this.hospital?.averageRating?.averageRating?.toFixed(1) || "N/A";
    },
    totalReviews() {
      return this.hospital?.averageRating?.totalReviews || 0;
    },
    patientsCount() {
      return this.doctor?.timeSlots?.[0]?.numberOfPatients || "N/A";
    },
    doctorImage() {
      // 1️⃣ Doctor photo exists and is not empty string
      if (this.doctor?.photo && this.doctor.photo.trim() !== "") {
        return this.doctor.photo;
      }

      // 2️⃣ Doctor gender present → show male/female icon
      if (this.doctor?.gender) {
        const gender = this.doctor.gender.toLowerCase();
        if (gender === "female") {
          return new URL("@/assets/img/female-doctor.png", import.meta.url)
            .href;
        }
        if (gender === "male") {
          return new URL("@/assets/img/male-doctor.png", import.meta.url).href;
        }
      }

      // 3️⃣ No photo and no gender → fallback to hospital photo
      if (this.hospital?.photo && this.hospital.photo.trim() !== "") {
        return this.hospital.photo;
      }

      // 4️⃣ Final fallback (if you want) → default male placeholder
      return new URL("@/assets/img/male-doctor.png", import.meta.url).href;
    },
    uniqueTimeSlotsByHospital() {
      const unique = [];
      const seenHospitals = new Set();

      if (this.doctor && Array.isArray(this.doctor.timeSlots)) {
        for (const slot of this.doctor.timeSlots) {
          if (!seenHospitals.has(slot.hospitalId)) {
            unique.push(slot);
            seenHospitals.add(slot.hospitalId);
          }
        }
      }

      return unique;
    },
  },
  methods: {
    getUniqueDays(days) {
      if (!Array.isArray(days)) return [];

      const dayMap = new Map();

      days.forEach((dayObj) => {
        if (!dayMap.has(dayObj.day)) {
          // Take the first entry for that day
          dayMap.set(dayObj.day, dayObj);
        } else {
          // Optionally merge logic if times differ
          const existing = dayMap.get(dayObj.day);
          // Example: pick the earliest start time and latest end time
          if (dayObj.startTime < existing.startTime) {
            existing.startTime = dayObj.startTime;
          }
          if (dayObj.endTime > existing.endTime) {
            existing.endTime = dayObj.endTime;
          }
        }
      });

      // Sort by weekday order (optional, for nice display)
      const order = [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
        "Sunday",
      ];

      return Array.from(dayMap.values()).sort(
        (a, b) => order.indexOf(a.day) - order.indexOf(b.day)
      );
    },
    async fetchDoctor() {
      this.loading = true;
      this.error = null;
      const doctorId = this.$route.params.id;

      try {
        const response = await DoctorsService.getById(doctorId);
        if (response && response.data) {
          this.doctor = response.data;

          // Build hospitalMap
          const hospitalsArray = Array.isArray(this.doctor.hospitals)
            ? this.doctor.hospitals
            : this.doctor.hospital
            ? [this.doctor.hospital]
            : [];

          this.hospitalMap = hospitalsArray.reduce((acc, hospital) => {
            if (hospital?.id) acc[hospital.id] = hospital;
            return acc;
          }, {});
        } else {
          this.error = "Doctor not found.";
          this.doctor = null;
        }
      } catch (err) {
        console.error(err);
        this.error = "Error fetching doctor details.";
        this.doctor = null;
      } finally {
        this.loading = false;
      }
    },
    formatSittingTime(timeRange) {
      if (!timeRange || typeof timeRange !== "string") return "";

      // Normalize and split
      const normalized = timeRange.replace(/\s+/g, " ").trim();
      const [startRaw, endRaw] = normalized
        .split(/to|-|–/i)
        .map((t) => t.trim());

      const formatTime = (timeStr) => {
        if (!timeStr) return "";

        let [hour, minute] = timeStr.split(":");
        hour = parseInt(hour);
        minute = parseInt(minute) || 0;

        const period = hour >= 12 ? "PM" : "AM";
        hour = hour % 12 || 12; // convert 0 to 12

        return `${hour}:${minute.toString().padStart(2, "0")} ${period}`;
      };

      const start = formatTime(startRaw);
      const end = formatTime(endRaw);

      if (start && end) return `${start} - ${end}`;
      if (start) return start;
      if (end) return end;

      return "";
    },
    dayNameMap() {
      return {
        MON: "Monday",
        TUE: "Tuesday",
        WED: "Wednesday",
        THU: "Thursday",
        FRI: "Friday",
        SAT: "Saturday",
        SUN: "Sunday",
      };
    },
    capitalizeFirst(str) {
      if (!str) return "";
      return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    },
    convertUtcToLocal(utcTime) {
      if (!utcTime) return "";

      // Create a date using today's date + UTC time
      const now = new Date();
      const [hours, minutes] = utcTime.split(":").map(Number);
      const utcDate = new Date(
        Date.UTC(
          now.getFullYear(),
          now.getMonth(),
          now.getDate(),
          hours,
          minutes
        )
      );

      // Convert to local time
      const localTime = new Date(
        utcDate.toLocaleString("en-US", {
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        })
      );

      // Format in 12-hour format with AM/PM
      const options = { hour: "numeric", minute: "2-digit", hour12: true };
      return localTime.toLocaleTimeString([], options);
    },
  },
  watch: {
    "$route.params.id": "fetchDoctor",
  },
  created() {
    this.fetchDoctor();
  },
};
</script>

<style scoped>
.bio-container {
  max-height: 780px;
  overflow-y: auto;
  padding: 5px 5px !important;
}
.doc-sp {
  font-size: 18px;
}
.doc-wd {
  width: 306px;
}
.doc-wd ul li {
  border: 0;
  padding: 5px 0 10px;
}
.doc-wd ul li .list-group-item {
  border: 0;
  padding: 5px 0 5px;
  border-bottom: 1px solid #0000003b;
  border-radius: 0;
}
.card p i {
  color: #3b74ba;
  width: 26px;
}
</style>
